import { memo } from 'react';
import LegacyImage from 'next/legacy/image';
import NextImage from 'next/image';
import styled, { createGlobalStyle } from 'styled-components';
import { ButtonEl } from 'components/site/site_sections';
import Button from 'components/global/Button/Primary';
import PromoCodeIndicator from 'components/pdp/promo-code-indicator';
import { Product } from 'entities/product';
import cloudinaryLoader from 'lib/image/cloudinary-loader';
import { StarRating } from 'components/pdp/star-rating';
import { SiteDot } from 'lib/svgs/site-dot';
import { createMarkup } from 'lib/utils/create-markup';
import { useAddToCartCta } from 'hooks/use-add-to-cart-cta';
import { mqXXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import { WithSiteLayout } from 'styles/commonCss';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';

const RatingContainer = styled.div`
  padding-top: 20px;
`;

type GridItemProps = {
  src: string;
  label: string;
};

const GridItem = memo(({ src, label }: GridItemProps) => (
  <div className='scrolly-icons-grid-item'>
    {src && (
      <LegacyImage
        src={src}
        width={40}
        height={40}
        loader={cloudinaryLoader}
        alt={`${label} icon`}
      />
    )}

    {!src && (
      <svg
        height='40'
        width='40'
        viewBox='-0.5 0 41 40'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle cx='20' cy='20' r='20' />
      </svg>
    )}
    <h6>{label}</h6>
  </div>
));

GridItem.displayName = 'GridItem';

const Pds08HeroStyles = createGlobalStyle`
.pds08-hero {
  .hero-banner {
    max-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-flow: column;

    @media ${mqLargeMin} {
      height: calc(100vh - 100px);
      margin-right: 30px;
      justify-content: none;
      flex-flow: row;
    }

    h1 {
      width: 100%;
      white-space: nowrap;
      position: relative;
      margin-bottom: 20px;

      span {
        display: block;
        font-size: var(--font-size-600);
        font-weight: 300;
        color: var(--text-primary);
        line-height: 1.1;

        &.age-clarification {
          padding-top: var(--spacing-8);
          padding-left: 2px;
          font-size: var(--font-size-400);
          font-weight: 400;
        }
      }

      @media ${mqLargeMin} {
        width: fit-content;
        margin-bottom: 0px;
        position: absolute;

        span {
          font-size: var(--font-size-1000);
        }
      }
    }
  }

  .image-container {
    margin: auto;
    margin-right: 1.5%;
    position: relative;
    width: 100%;
    height: 644px;
    max-height: 80vh;

    @media ${mqLargeMin} {
      width: 50%;
      margin-left: 25%;
    }

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .cta-container {
    position: relative;
  }

  .site-buy {
  ${WithSiteLayout}
  position: relative;
  display: block;
  z-index: 2;

  @media ${mqLargeMin} {
    justify-content: flex-end;
    display: flex;
    min-width: 300px;
    margin-top: 0;
  }

  @media ${mqXXLargeMin} {
    justify-content: flex-end;
    display: flex;
    margin-top: 0;
  }
}
.srp0text .site-buy {
  @media ${mqLargeMin} {
    justify-content: flex-start;
  }

  @media ${mqXXLargeMin} {
    justify-content: flex-start;
  }
}
.site-buy .container {
  width: 100% !important;
  position: relative;

  @media ${mqLargeMin} {
    width: 300px;
  }
}
.srp0text .site-buy .container {
  width: 100%;
  position: relative;
}
.site-buy .blurb {
  margin: var(--spacing-24) 0 0;
  p {
    font-size: 13px;
    margin-top: var(--spacing-8)/2;
    * {
      font-size: 13px;
    }
    strong {
      padding-right: 5px;
    }
    &.bg-green {
      display: inline-block;
      background: #b9f6a3;
      padding: 3px 5px 1px;
      margin-top: 0;
    }
    & span {
      font-weight: 500;
      font-size: 13px;
    }
  }
}
.site-buy .list {
  display: block;
  position: relative;
  margin: var(--spacing-24) 0 0;
  width: 100%;

  @media ${mqLargeMin} {
    transition: all .25s ease;
  }
}
.site-buy .list li {
  font-size: var(--font-size-350);
  line-height: 1.75;

  a {
    all: unset;
    text-decoration: underline;
    cursor: pointer;
  }
}
.site-buy .button {
  position: relative;
  text-transform: inherit;
  padding: var(--spacing-24) 0;
  margin: var(--spacing-8) 0 0;
  width: 100%;

  &:focus {
    outline: solid 2px black;
    outline-offset: 3px;
  }
}

.page-vaginal-synbiotic {
  .site-buy .button {
    margin: var(--spacing-24) 0 0;
  }
}

.site-buy .button i {
  transform: scale(0.8);
  padding: 0 var(--spacing-24);
}
.site-buy .button * {
  font-size: var(--font-size-400);
  font-weight: 500;
  color: #fff;

  @media ${mqLargeMin} {
    font-weight: 500;
  }
}
.site-buy .cta-button {
  width: 100%;
  margin: var(--spacing-8) 0 0;
  color: var(--actions-inverse);

  * {
    color: var(--actions-inverse);
  }

  svg {
    stroke: none;
    margin: 0 var(--spacing-8);
  }
}

  .site-buy {
    padding: 0;

    .container {
      position: relative;

      @media (min-width: 64em) and (max-width: 90em) and (max-height: 45em) {
        bottom: 20px;
      }

      @media ${mqLargeMin} {
        transform: translateY(50%);
        bottom: 0;
        position: absolute;
      }
    }

    .blurb {
      .bg-green {
        font-size: var(--font-size-400);
        color: var(--text-primary);
        background: none !important;
        padding: 0 !important;
        margin-bottom: 1em;
      }

      p {
        color: var(--text-primary);
      }

      div {
        margin-bottom: 30px;

        button {
          margin: 0;
        }

        span {
          font-size: 13px;
          color: var(--text-primary);
          text-decoration: underline;
        }
      }
    }

    .list {
      li {
        color: var(--text-primary);

        a {
          all: unset;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }

    .button {
      margin-bottom: 30px;

      &:focus,
      &:active {
        background-color: #fff;
        * {
          color: var(--text-primary);
        }
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: #fff;
          * {
            color: var(--text-primary);
          }
        }
      }
    }
  }

  .hero-scrolly {
    width: calc(100% + 20px);
    background-color: var(--guidance-secondary);
    margin: 60px -10px 0;
    border-radius: 8px;
    padding: 30px 15px;
    display: flex;
    flex-direction: column;

    @media ${mqLargeMin} {
      flex-direction: row;
      padding: 50px;
      border-radius: 16px;
      margin-top: 0px;
    }

    h2 {
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.7px;
      margin: 0 0 20px;
      color: var(--text-primary);
      text-transform: uppercase;
    }

    li {
      font-size: 20px;
      font-weight: 300;
      color: var(--text-primary);
      padding: 10px 0;

      @media ${mqLargeMin} {
        font-size: 30px;
      }

      &:not(:last-of-type) {
        border-bottom: 1px solid var(--outline-neutral);
      }
    }

    .left {
      @media ${mqLargeMin} {
        max-width: 50%;
        margin-right: 80px;
      }
    }

    .right {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
    }
  }

  .scrolly-icons {
    h5 {
      font-weight: 400;
      color: var(--text-primary);
      margin: 30px 0;
    }

    margin-bottom: 30px;
  }

  .scrolly-icons-grid {
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 24px;

    @media ${mqLargeMin} {
      grid-template-rows: repeat(2, 1fr);
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .scrolly-icons-grid-item {
    display: flex;
    align-items: center;

    h6,
    .h6-style {
      font-size: 14px;
      margin-left: 8px;
      color: var(--text-primary);
      font-weight: 400;
    }

    svg {
      fill: none;
      stroke: var(--outline-primary);
    }
  }

  span:not(.site-buy *) {
    color: var(--text-primary) !important;
  }
}

`;

type PDS08HeroProps = {
  product: Product;
  isRefill?: boolean | false;
  type?: 'pds-08' | string;
  siteBuy: any;
  priceString: string;
};

const PDS08Hero = ({
  product,
  isRefill = false,
  siteBuy,
  priceString,
}: PDS08HeroProps) => {
  const buyContent = siteBuy?.fields;
  const buyModal = buyContent?.modal;
  const buyButton = product?.button || buyContent?.button;
  const ctaLabel = buyButton?.[0] ?? 'Start Now';
  const ctaPrice = priceString || buyButton?.[1];
  const blurb: string[] = buyContent?.blurb ?? [];
  const reassuranceList: string[] = buyContent?.list ?? [];

  const { productId, cartHref, handleAddToCart } = useAddToCartCta({
    product,
    buttonName: 'PDS start now',
  });

  const iconData = [
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/no-sugar-added.svg',
      label: 'No Added Sugar',
    },
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/soy-tested.svg',
      label: 'Soy-tested',
    },
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/gluten-tested.svg',
      label: 'Gluten-tested',
    },
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/dairy-tested.svg',
      label: 'Dairy-tested',
    },
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/nuts-tested.svg',
      label: 'Nuts-tested',
    },
    {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/sesame-tested.svg',
      label: 'Sesame-tested',
    },
  ];

  return (
    <section className='pds08-hero'>
      <Pds08HeroStyles />
      <div className='container'>
        <div className='hero-banner'>
          <h1>
            <span>PDS-08®</span>
            <span>Pediatric</span>
            <span>Daily Synbiotic</span>
            {isRefill && <span>Monthly Refill</span>}
            <span className='age-clarification'>For Kids + Teens (3-17)</span>
            <RatingContainer>
              <StarRating
                productSku={PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC}
              />
            </RatingContainer>
          </h1>
          <div className='image-container'>
            <NextImage
              src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto,w_1000/v1649362831/PDS%20PDP/pds08_pdp_hero.png'
              alt='PDS-08 Packaging'
              layout='fill'
              priority={true}
            />
          </div>

          <div className='cta-container'>
            {buyContent && product && (
              <div className='site-buy'>
                <div className='container'>
                  {blurb.length > 0 && (
                    <div className='blurb'>
                      {blurb.map((v, i) => (
                        <p
                          key={v + i}
                          className={
                            (blurb.length > 1 && i === 0 && 'bg-green') || ''
                          }
                          dangerouslySetInnerHTML={createMarkup(v)}
                        />
                      ))}
                      {buyModal && (
                        <ButtonEl
                          data={{
                            fields: {
                              label: buyModal.label,
                              modal: buyModal.modal,
                            },
                          }}
                        />
                      )}
                    </div>
                  )}
                  <PromoCodeIndicator productId={productId} />
                  <Button
                    href={cartHref}
                    onClick={handleAddToCart}
                    className='cta-button'
                    size='md'
                    data-testid='add-to-cart-cta'
                  >
                    <span>{ctaLabel}</span>
                    <SiteDot height={12} width={12} color='currentColor' />
                    <span>{ctaPrice}</span>
                  </Button>
                  {reassuranceList.length > 0 && (
                    <ul className='list'>
                      {reassuranceList.map((item, i) => (
                        <li
                          key={item + i}
                          dangerouslySetInnerHTML={createMarkup(item)}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='hero-scrolly'>
          <div className='left'>
            <h2>Need-to-Knows</h2>
            <ul className='scrolly-list'>
              <li>Formulated for benefits in and beyond the gut.</li>
              <li>5g of prebiotic fiber per serving.</li>
              <li>No observed side effects or GI distress.</li>
              <li>Engineered to survive digestion.</li>
              <li>No refrigeration necessary.</li>
            </ul>
          </div>
          <div className='right'>
            <div className='scrolly-icons'>
              <div className='scrolly-icons-grid'>
                {iconData.map(({ src, label }) => (
                  <GridItem key={label} label={label} src={src} />
                ))}
              </div>
            </div>

            <ButtonEl
              data={{
                fields: {
                  icon: 'plus_circle',
                  altIcon: 'minus_circle',
                  label: 'PDS-08® Supplement Facts',
                  modal: 'modal-pds08-facts',
                },
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

PDS08Hero.defaultProps = {
  isRefill: false,
  type: 'pds-08',
};

export default memo(PDS08Hero);
